'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface FeedbackSubmission {
  id: string;
  overallRating: number;
  npsScore: number | null;
  serviceRatings: Record<string, number>;
  answers: Record<string, string>;
  openFeedback: string;
  email: string;
  submittedAt: string;
}

const mockSubmissions: FeedbackSubmission[] = [
  { id: 'FB-001', overallRating: 5, npsScore: 9, serviceRatings: { booking_ease: 5, price_value: 4, customer_support: 5, website_experience: 5 }, answers: { visit_purpose: 'Leisure / Holiday', booking_frequency: '2–4 times a year', recommend_likelihood: 'Definitely yes' }, openFeedback: 'Absolutely brilliant service! The AI recommendations were spot on.', email: 'john@example.com', submittedAt: '2026-03-01T10:23:00Z' },
  { id: 'FB-002', overallRating: 4, npsScore: 8, serviceRatings: { booking_ease: 4, price_value: 4, customer_support: 3, website_experience: 4 }, answers: { visit_purpose: 'Business Travel', booking_frequency: '5+ times a year', recommend_likelihood: 'Probably yes' }, openFeedback: 'Good overall. Support response time could be faster.', email: '', submittedAt: '2026-03-01T14:45:00Z' },
  { id: 'FB-003', overallRating: 5, npsScore: 10, serviceRatings: { booking_ease: 5, price_value: 5, customer_support: 5, website_experience: 5 }, answers: { visit_purpose: 'Visiting Family & Friends', booking_frequency: 'Once a year', recommend_likelihood: 'Definitely yes' }, openFeedback: 'Best travel booking site I have used. Will definitely recommend!', email: 'sarah@example.com', submittedAt: '2026-03-02T09:12:00Z' },
  { id: 'FB-004', overallRating: 3, npsScore: 6, serviceRatings: { booking_ease: 3, price_value: 3, customer_support: 2, website_experience: 4 }, answers: { visit_purpose: 'Leisure / Holiday', booking_frequency: 'First time', recommend_likelihood: 'Not sure' }, openFeedback: 'Decent but prices were a bit higher than expected.', email: '', submittedAt: '2026-03-02T16:30:00Z' },
  { id: 'FB-005', overallRating: 5, npsScore: 9, serviceRatings: { booking_ease: 5, price_value: 4, customer_support: 5, website_experience: 5 }, answers: { visit_purpose: 'Business Travel', booking_frequency: '5+ times a year', recommend_likelihood: 'Definitely yes' }, openFeedback: 'Seamless experience from search to booking confirmation.', email: 'mike@example.com', submittedAt: '2026-03-03T08:00:00Z' },
];

const ratingColors = ['#EF4444', '#F97316', '#F59E0B', '#84CC16', '#10B981'];

const FeedbackSurveyAdmin = () => {
  const [submissions, setSubmissions] = useState<FeedbackSubmission[]>(mockSubmissions);
  const [selected, setSelected] = useState<FeedbackSubmission | null>(null);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('feedbackSubmissions') || '[]');
    if (stored.length > 0) setSubmissions([...stored, ...mockSubmissions]);
  }, []);

  const avgRating = (submissions.reduce((s, r) => s + r.overallRating, 0) / submissions.length).toFixed(1);
  const avgNPS = submissions.filter((s) => s.npsScore !== null).reduce((sum, s) => sum + (s.npsScore || 0), 0) / submissions.filter((s) => s.npsScore !== null).length;
  const promoters = submissions.filter((s) => (s.npsScore || 0) >= 9).length;
  const detractors = submissions.filter((s) => (s.npsScore || 0) <= 6).length;
  const npsScore = Math.round(((promoters - detractors) / submissions.length) * 100);

  const ratingDist = [1, 2, 3, 4, 5].map((r) => ({
    rating: `${r}★`,
    count: submissions.filter((s) => s.overallRating === r).length,
  }));

  const npsData = [
    { name: 'Promoters (9–10)', value: promoters, color: '#10B981' },
    { name: 'Passives (7–8)', value: submissions.filter((s) => (s.npsScore || 0) >= 7 && (s.npsScore || 0) <= 8).length, color: '#F59E0B' },
    { name: 'Detractors (0–6)', value: detractors, color: '#EF4444' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-black text-foreground">Feedback Survey Responses</h2>
          <p className="text-sm text-muted-foreground mt-1">{submissions.length} total responses collected</p>
        </div>
        <a
          href="/feedback"
          target="_blank"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-bold rounded-lg text-sm hover:bg-secondary transition-colors"
        >
          <Icon name="ArrowTopRightOnSquareIcon" size={16} />
          View Survey
        </a>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Avg Rating', value: `${avgRating}/5`, icon: 'StarIcon', bg: 'bg-yellow-50', text: 'text-yellow-600' },
          { label: 'NPS Score', value: npsScore.toString(), icon: 'ArrowTrendingUpIcon', bg: 'bg-green-50', text: 'text-green-600' },
          { label: 'Avg NPS', value: `${avgNPS.toFixed(1)}/10`, icon: 'ChartBarIcon', bg: 'bg-blue-50', text: 'text-blue-600' },
          { label: 'Total Responses', value: submissions.length.toString(), icon: 'ChatBubbleBottomCenterTextIcon', bg: 'bg-purple-50', text: 'text-purple-600' },
        ].map((card, i) => (
          <div key={i} className="bg-white rounded-xl p-4 border border-border">
            <div className={`w-10 h-10 ${card.bg} rounded-lg flex items-center justify-center mb-3`}>
              <Icon name={card.icon as any} size={20} className={card.text} />
            </div>
            <div className="text-2xl font-black text-foreground">{card.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-border p-5">
          <h3 className="text-lg font-heading font-black text-foreground mb-4">Rating Distribution</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={ratingDist} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="rating" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v: number) => [v, 'Responses']} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {ratingDist.map((_, i) => <Cell key={i} fill={ratingColors[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-border p-5">
          <h3 className="text-lg font-heading font-black text-foreground mb-4">NPS Breakdown</h3>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="50%" height={160}>
              <PieChart>
                <Pie data={npsData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                  {npsData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v: number) => [v, 'Users']} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {npsData.map((d, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-xs text-muted-foreground">{d.name}</span>
                  </div>
                  <span className="text-xs font-black text-foreground">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Responses List */}
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="p-5 border-b border-border">
          <h3 className="text-lg font-heading font-black text-foreground">Recent Responses</h3>
        </div>
        <div className="divide-y divide-border">
          {submissions.slice(0, 10).map((sub) => (
            <div
              key={sub.id}
              className="p-4 hover:bg-surface transition-colors cursor-pointer"
              onClick={() => setSelected(selected?.id === sub.id ? null : sub)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map((s) => (
                      <svg key={s} width={14} height={14} viewBox="0 0 24 24" fill={s <= sub.overallRating ? '#F59E0B' : '#E5E7EB'} stroke="none">
                        <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                      </svg>
                    ))}
                  </div>
                  <span className="text-sm font-bold text-foreground">{sub.id}</span>
                  {sub.npsScore !== null && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      sub.npsScore >= 9 ? 'bg-green-100 text-green-700' : sub.npsScore >= 7 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                    }`}>NPS {sub.npsScore}</span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{new Date(sub.submittedAt).toLocaleDateString()}</span>
              </div>
              {sub.openFeedback && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{sub.openFeedback}</p>
              )}
              {selected?.id === sub.id && (
                <div className="mt-4 pt-4 border-t border-border space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(sub.serviceRatings).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between bg-surface rounded-lg px-3 py-2">
                        <span className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="text-xs font-black text-amber-500">{val}★</span>
                      </div>
                    ))}
                  </div>
                  {Object.entries(sub.answers).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span>
                      <span className="text-xs font-bold text-foreground">{val}</span>
                    </div>
                  ))}
                  {sub.email && <div className="text-xs text-muted-foreground">Email: {sub.email}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeedbackSurveyAdmin;
